import { useTranslations } from 'next-intl';
import { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useClickAway } from 'react-use';

import { CopyTradePortfolio } from '@flipster/apis/v1/copy-trade/types';
import { FormattedMessage } from '@flipster/i18n';
import Formatted from '@flipster/react/design-utils/formatted';
import { IsomorphicLink } from '@flipster/react/design-utils/isomorphic-link';
import UserIcon from '@flipster/react/icon/generated/UserIcon';
import { css, cx } from '@flipster/styled-system/css';
import { hstack, stack } from '@flipster/styled-system/patterns';
import { scrollbar } from '@flipster/styled-system/recipes';
import { token } from '@flipster/styled-system/tokens';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { isNullable } from '@flipster/utils/nullable';

import ClearableSearchInput from '../../../../input/clearable-search-input';
import { ProfileIcon } from '../../../shared/profile-icon';
import { getPortfolioPerformance } from '../utils';

/** Suggestion metrics always use 30D, independent of the toolbar period select. */
const suggestionPeriod = '30d' as const;
const suggestionDays = 30;

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  suggestions: CopyTradePortfolio[];
}

export const SearchInput = ({ value, onChange, suggestions }: SearchInputProps) => {
  const t = useTranslations();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const showSuggestions = isOpen && value.trim().length > 0 && suggestions.length > 0;

  useClickAway(containerRef, () => setIsOpen(false));

  useEffect(() => {
    if (!value.trim()) setIsOpen(false);
  }, [value]);

  return (
    <div
      ref={containerRef}
      className={css({ pos: 'relative', w: '100%', tablet: { maxW: 380 } })}
      onKeyDown={(event: KeyboardEvent) => {
        if (event.key === 'Escape') setIsOpen(false);
      }}
    >
      <ClearableSearchInput
        value={value}
        onChange={(next) => {
          onChange(next);
          if (next.trim()) setIsOpen(true);
        }}
        onClear={() => {
          onChange('');
          setIsOpen(false);
        }}
        placeholder={t('Copy.r260709.Search_placeholder_web')}
        css={{ w: '100%' }}
      />
      {showSuggestions && (
        <div
          className={cx(
            stack({
              pos: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              zIndex: 10,
              bg: 'background.default.level-3',
              rounded: 8,
              borderWidth: '1px',
              borderStyle: 'solid',
              borderColor: 'border.default.level-3',
              gap: 2,
              maxH: 540,
              overflowY: 'auto',
              p: 4,
            }),
            scrollbar(),
          )}
        >
          {suggestions.map((portfolio) => (
            <div key={portfolio.portfolioId}>
              <SearchSuggestionRow portfolio={portfolio} onNavigate={() => setIsOpen(false)} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const SearchSuggestionRow = ({ portfolio, onNavigate }: { portfolio: CopyTradePortfolio; onNavigate: () => void }) => {
  const performance = getPortfolioPerformance(portfolio, suggestionPeriod);
  const { nickname, profileIconUrl, currentCopierCount, maxCopierCount, userProfileId } = portfolio;

  return (
    <IsomorphicLink
      href={`/user/profile/${userProfileId}`}
      onClick={onNavigate}
      className={stack({
        gap: 4,
        py: 12,
        px: 16,
        _hover: { bg: 'background.default.level-2' },
        rounded: 4,
      })}
    >
      <div className={hstack({ gap: 10 })}>
        <ProfileIcon profileIconUrl={profileIconUrl} size={28} />
        <div className={stack({ gap: 2, flex: 1, minW: 0 })}>
          <p
            className={css({
              textStyle: 'Text/M/Strong',
              color: 'content.default.level-5',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            })}
          >
            {nickname}
          </p>
          <div className={hstack({ gap: 2 })}>
            <UserIcon size={10} color={token('colors.content.default.level-4')} />
            <p className={css({ textStyle: 'Text/S/Monospace/Regular' })}>
              <span className={css({ color: 'content.default.level-4' })}>{currentCopierCount}</span>
              <span className={css({ color: 'content.default.level-1' })}>/{maxCopierCount}</span>
            </p>
          </div>
        </div>
      </div>
      <div className={stack({ gap: 2, textStyle: 'Text/M/Regular' })}>
        <div className={hstack({ h: 18, justify: 'space-between', gap: 16 })}>
          <p className={css({ color: 'content.default.level-2', textStyle: 'Text/S/Regular' })}>
            <FormattedMessage id='Copy.r251107.days_days_roi' values={{ days: suggestionDays }} />
          </p>
          {isNullable(performance?.roiPct) ? (
            <span className={css({ color: 'content.default.level-5' })}>--</span>
          ) : (
            <Formatted.Percent
              value={performance.roiPct}
              precision={2}
              toFixed
              withColor
              withArrow
              css={{ color: 'content.default.level-5' }}
            />
          )}
        </div>
        <div className={hstack({ h: 18, justify: 'space-between', gap: 16 })}>
          <p className={css({ color: 'content.default.level-2', textStyle: 'Text/S/Regular' })}>
            <FormattedMessage
              id='Copy.r251107.days_days_pnl_symbol'
              values={{ days: suggestionDays, symbol: UsdtCurrency }}
            />
          </p>
          {isNullable(performance?.pnlUsdt) ? (
            <span className={css({ color: 'content.default.level-5' })}>--</span>
          ) : (
            <Formatted.Number
              value={performance.pnlUsdt}
              precision={UsdtPrecision}
              toFixed
              withPlus
              withColor
              css={{ color: 'content.default.level-5' }}
            />
          )}
        </div>
      </div>
    </IsomorphicLink>
  );
};
